/**
 * TabBar — draggable tabs for a single pane group.
 *
 * Accepts a `group` prop and renders only that group's tabs. Two sortable
 * zones: pinned tabs sort among pinned, unpinned among unpinned.
 *
 * Only the side pane renders one: the primary pane has no strip, since the
 * sidebar already says where you are. Below the nav breakpoint it is not shown
 * at all: a phone navigates as a stack, not as tabs. Overflow scrolls with the
 * app's standard themed scrollbar.
 *
 * How the strip and its tabs paint lives in `tabClasses.ts`.
 */
import React, { useMemo, useState } from 'react';

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAppStore } from '@/stores/useAppStore';
import { DomainIcon, Icons } from '@shared/components/ui';
import { ATTENTION_ROUTES } from '@shared/constants/routes';

import { badgeClasses, STRIP_PAINT, tabClasses, tabDomain } from './tabClasses';
import { TabContextMenu } from './TabContextMenu';
import { useWorkspaceNav } from '../hooks/useWorkspaceNav';
import { useWorkspaceStore, tabsForGroup } from '../stores/useWorkspaceStore';

import type { PaneGroup, WorkspaceTab } from '../types';
import type { DragEndEvent } from '@dnd-kit/core';

/**
 * Tabs that carry the unread-inbox count: the surface root, Dismissed Nows,
 * and any single message opened in place. The `/attention/mail/*` views are
 * deliberately excluded — Sent and Drafts have no unread count to show.
 */
const INBOX_TAB_PATHS = new Set<string>([ATTENTION_ROUTES.root, ATTENTION_ROUTES.history]);
const MESSAGE_PATH_PREFIX = ATTENTION_ROUTES.message.replace(':id', '');

/** The unread-inbox count an inbox tab shows as a badge, or null for none. */
function useInboxCount(tab: WorkspaceTab): number | null {
  const inboxCount = useAppStore(s => s.navCounts.emailInbox);
  const isInboxTab = INBOX_TAB_PATHS.has(tab.path) || tab.path.startsWith(MESSAGE_PATH_PREFIX);
  return isInboxTab && inboxCount != null && inboxCount > 0 ? inboxCount : null;
}

/** A tab's leading glyph: the pin for a pinned tab, else its domain icon. */
const TabGlyph: React.FC<{ tab: WorkspaceTab }> = ({ tab }) => {
  if (tab.pinned) return <Icons.Pin size="xs" className="shrink-0" />;
  const domain = tabDomain(tab);
  return domain ? <DomainIcon domain={domain} size="xs" className="shrink-0" /> : null;
};

/** A single sortable tab. */
const SortableTab: React.FC<{
  tab: WorkspaceTab;
  isActive: boolean;
  isFocused: boolean;
  onSwitch: () => void;
  onClose: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}> = ({ tab, isActive, isFocused, onSwitch, onClose, onContextMenu }) => {
  const count = useInboxCount(tab);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tab.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <button
      ref={setNodeRef}
      style={style}
      type="button"
      data-active={isActive || undefined}
      onClick={onSwitch}
      onContextMenu={onContextMenu}
      className={tabClasses({ pinned: tab.pinned, isActive, isFocused })}
      {...attributes}
      {...listeners}
    >
      <TabGlyph tab={tab} />

      <span className={tab.pinned ? 'max-w-[80px] truncate' : 'max-w-[180px] truncate'}>
        {tab.title}
      </span>

      {/* The space keeps the accessible name "Inbox 3"; flex layout drops it visually. */}
      {count !== null && (
        <>
          {' '}
          <span className={badgeClasses(isActive)}>{count}</span>
        </>
      )}

      {!tab.pinned && (
        <span
          role="button"
          tabIndex={0}
          aria-label={`Close ${tab.title}`}
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              onClose();
            }
          }}
          className={`text-muted-foreground hover:text-foreground hover:bg-secondary -mr-1 ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm transition-opacity ${
            isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          <Icons.X size="xs" />
        </span>
      )}
    </button>
  );
};

interface TabBarProps {
  group: PaneGroup;
}

export const TabBar: React.FC<TabBarProps> = ({ group }) => {
  const allTabs = useWorkspaceStore(s => s.tabs);
  const primaryActiveTabId = useWorkspaceStore(s => s.primaryActiveTabId);
  const secondaryActiveTabId = useWorkspaceStore(s => s.secondaryActiveTabId);
  const focusedGroup = useWorkspaceStore(s => s.focusedGroup);
  const reorderTabs = useWorkspaceStore(s => s.reorderTabs);
  const setFocusedGroup = useWorkspaceStore(s => s.setFocusedGroup);
  const { switchToTab, closeTab } = useWorkspaceNav();

  const groupTabs = useMemo(() => tabsForGroup(allTabs, group), [allTabs, group]);
  const activeTabId = group === 'primary' ? primaryActiveTabId : secondaryActiveTabId;
  const isFocused = group === focusedGroup;

  const [contextMenu, setContextMenu] = useState<{
    tab: WorkspaceTab;
    x: number;
    y: number;
  } | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const pinnedTabs = useMemo(() => groupTabs.filter(t => t.pinned), [groupTabs]);
  const unpinnedTabs = useMemo(() => groupTabs.filter(t => !t.pinned), [groupTabs]);
  const pinnedIds = useMemo(() => pinnedTabs.map(t => t.id), [pinnedTabs]);
  const unpinnedIds = useMemo(() => unpinnedTabs.map(t => t.id), [unpinnedTabs]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    reorderTabs(active.id as string, over.id as string);
  };

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {/* Click the tab bar background to focus this group */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          data-testid={`tab-strip-${group}`}
          className={`${STRIP_PAINT} hidden shrink-0 overflow-x-auto nav:flex`}
          onClick={() => setFocusedGroup(group)}
        >
          {/* Pinned zone */}
          {pinnedIds.length > 0 && (
            <SortableContext items={pinnedIds} strategy={horizontalListSortingStrategy}>
              {pinnedTabs.map(tab => (
                  <SortableTab
                    key={tab.id}
                    tab={tab}
                    isActive={tab.id === activeTabId}
                    isFocused={isFocused}
                    onSwitch={() => switchToTab(group, tab.id)}
                    onClose={() => closeTab(tab.id)}
                    onContextMenu={e => {
                      e.preventDefault();
                      setContextMenu({ tab, x: e.clientX, y: e.clientY });
                    }}
                  />
                ))}
            </SortableContext>
          )}

          {/* Separator */}
          {pinnedIds.length > 0 && unpinnedIds.length > 0 && (
            <div className="bg-sidebar-border mx-1 h-4 w-px shrink-0" />
          )}

          {/* Unpinned zone */}
          <SortableContext items={unpinnedIds} strategy={horizontalListSortingStrategy}>
            {unpinnedTabs.map(tab => (
                <SortableTab
                  key={tab.id}
                  tab={tab}
                  isActive={tab.id === activeTabId}
                  isFocused={isFocused}
                  onSwitch={() => switchToTab(group, tab.id)}
                  onClose={() => closeTab(tab.id)}
                  onContextMenu={e => {
                    e.preventDefault();
                    setContextMenu({ tab, x: e.clientX, y: e.clientY });
                  }}
                />
              ))}
          </SortableContext>
        </div>
      </DndContext>

      {contextMenu && (
        <TabContextMenu
          tab={contextMenu.tab}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
};
